import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { EApiMiddlewareMethods } from "../../network/networkHandler.types";
import { saveORExportFile, transformSelectValues } from "../../utils/helpers.utils";
import { DriverHelperTipState, TipData } from "./DriverhelperTipSlice.type";
import { cWingsServiceBaseURL, expressDriverBaseURL } from "../../config/Api.config";

import AppConstants from "../../constants";
import customNetworHandler from "../../network/customNetworHandler";
import { ExtendedPayloadFilters } from "DriverHelperTip/DriverHelperTipInterface";
import { getCountryUrlByKey } from "utils/urlUtils";
import { COUNTRY_URL_KEYS } from "constants/CommonConstants";

// Define request and response types
interface FetchTipRecordsRequest {
  filters: ExtendedPayloadFilters;
}

interface FetchTipRecordsResponse {
  results: {
    elements: TipData[];
    pageNumber: number;
    totalElements: number;
    totalPages: number;
    filters?: {
      assigneeTypes?: string[];
      vendor?: string[];
      status?: string[];
    };
  };
}

interface ExportTipDataRequest {
  payload: ExtendedPayloadFilters;
}

interface ExportTipDataResponse {
  headers: Record<string, string>;
  data: Blob;
}

// Async thunk for fetching tip records
export const fetchTipRecords = createAsyncThunk<
  FetchTipRecordsResponse,
  FetchTipRecordsRequest,
  { rejectValue: { error: string; message: string } }
>(
  "driverHelperTip/FetchTipRecords",
  async ({ filters }, { rejectWithValue }) => {
    try {
      const request = {
        url: "/assignee-tip/filter",
        method: EApiMiddlewareMethods.POST,
        data: filters,
      };
      const countryUrl = getCountryUrlByKey(COUNTRY_URL_KEYS.EWINGS_DRIVER);
      const url = countryUrl || expressDriverBaseURL;
      const { data } = await customNetworHandler(
          request, 
          url, 
          countryUrl ? COUNTRY_URL_KEYS.EWINGS_DRIVER : undefined
      );
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Async thunk for exporting tip data to Excel
export const exportTipDataToExcel = createAsyncThunk<
  ExportTipDataResponse,
  ExportTipDataRequest,
  { rejectValue: { error: string; message: string } }
>(
  "driverHelperTip/ExportTipDataToExcel",
  async ({payload}, { rejectWithValue }) => {
    try {
      const request = {
        url: "/fulfilment/v2/export/assignee-tips",
        method: EApiMiddlewareMethods.POST,
        data: payload,
        responseType: "blob",
        headers: {
          generateResponse: false,
        },
      };
      const response = await customNetworHandler(request, cWingsServiceBaseURL);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const resetExportInitiated = createAsyncThunk("driverHelperTip/ResetExportInitiated", () => {  
  return false;
});

// Initial state with proper types
const initialState: DriverHelperTipState = {
  loading: false,
  errorCode: "",
  error: "",
  tipData: { elements: [], pageNumber: 0, totalElements: 0, totalPages: 0 },
  filterDropdowns: {
    assigneeType: [],
    assigneeVendor: [],
    consignmentStatus: [],
  },
  exportInitiated: false,
  reportId: "", 
};

// Slice definition
const DriverHelperTipSlice = createSlice({
  name: "driverHelperTip",
  initialState,
  reducers: {},
  extraReducers: (builders) => {
    builders
      .addCase(fetchTipRecords.pending, (state) => {
        state.loading = true;
        state.errorCode = "";
        state.error = "";
      })
      .addCase(fetchTipRecords.fulfilled, (state, action) => {
        const {
          payload,
          meta: {
            arg: {filters}
        }
      } = action;
        state.loading = false;
        if (payload?.results) {
          state.tipData = {
            ...payload,
            elements:
              filters.pageNumber === 0
                ? (payload && payload.results && Array.isArray(payload.results.elements) ? [...payload.results.elements] : [])
                : [
                    ...(Array.isArray(state.tipData.elements) ? state.tipData.elements : []),
                    ...(payload && payload.results && Array.isArray(payload.results.elements) ? payload.results.elements : [])
                  ],
            pageNumber: payload?.results?.pageNumber,
            totalElements: payload?.results?.totalElements,
            totalPages: payload?.results?.totalPages
          };
        }
        if (payload?.results?.filters) {
          state.filterDropdowns = {
            assigneeType: transformSelectValues(
              payload.results.filters.assigneeTypes || []
            ),
            assigneeVendor: transformSelectValues(
              payload.results.filters.vendor || []
            ),
            consignmentStatus: transformSelectValues(
              payload.results.filters.status || []
            ),
          };
        }
      })
      .addCase(fetchTipRecords.rejected, (state, action) => {
        state.loading = false;
        const errorPayload: any = action?.payload;
        state.errorCode = errorPayload
          ? errorPayload?.error
          : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
        state.error =
          errorPayload && errorPayload?.message
            ? errorPayload?.message || ""
            : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
      })
      /** exportUserInformationToExcel */
      .addCase(exportTipDataToExcel.pending, (state) => {
        state.loading = true;
        state.error = "";
        state.errorCode = "";
      })
      .addCase(exportTipDataToExcel.fulfilled, (state, action) => {
        const { payload } = action;
        const {headers, data} = payload;
        state.loading = false;
        if (payload) {
          const { exportInitiated, requestId } = saveORExportFile(
            data,
            headers,
            "driverHelperTip",
          );
          state.exportInitiated = exportInitiated;
          if (requestId) {
            state.reportId = requestId;
          }
        }
      })
      .addCase(exportTipDataToExcel.rejected, (state, action) => {
        const errorPayload: any = action.payload;
        state.loading = false;
        state.errorCode = errorPayload
          ? errorPayload.error
          : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
        state.error = errorPayload
          ? errorPayload.message
          : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
      })
      .addCase(resetExportInitiated.fulfilled, (state) => {
              state.exportInitiated = false;
      })  
  },
});

export default DriverHelperTipSlice.reducer;
